import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';

type NumberFieldProps = {
  field: { label?: string };
  name: string;
  /**
   * Optional because a sub-field of a Puck `object` field is read out of the field store *by path*,
   * which yields `undefined` when the parent object is not in the stored props -- the block-level
   * `defaultProps` merge does not reach inside an object field to supply it.
   *
   * Seeding every default layout (ECOMM-13011) is what stops that happening for data this codebase
   * authors, and is the actual fix for the crash that surfaced it. This stays optional because the
   * type would otherwise be false: unvalidated stored JSON can still omit the key, and the widget
   * has to render *some* number regardless. See `absentValue` for which number.
   */
  value?: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

/**
 * Returns a Puck custom field render function for numeric CSS values.
 * The `unit` parameter (e.g. 'px', 'rem') is shown as a suffix badge
 * and appended to the stored value.
 *
 * `absentValue` is the number the input displays when the stored value is missing. It exists to keep
 * the panel *honest*, not to supply a default: it must equal whatever the renderer falls back to for
 * that same absent value, or the editor reports one number while the live page renders another --
 * the asymmetry `section.tsx`'s padding comment warns about.
 *
 * Defaults to `0${unit}`, already correct for every spacing caller (`spacingToCss` resolves an
 * absent box to `NO_SPACING`). `border.width` is the one exception: `borderStyle` falls back to
 * `DEFAULT_BORDER.width` (1px), so the two border callers pass it explicitly.
 *
 * Note this is display-only and is never written to the store, so opening a panel cannot persist a
 * value the author did not choose.
 */
export function NumberField(unit: string, absentValue = `0${unit}`) {
  return function NumberFieldWidget({ field, name, value, onChange, readOnly }: NumberFieldProps): JSX.Element {
    const numericValue = parseFloat(value ?? absentValue) || 0;

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.currentTarget.value;
        onChange(raw === '' ? `0${unit}` : `${raw}${unit}`);
      },
      [onChange]
    );

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type='number'
            value={numericValue}
            onChange={handleChange}
            disabled={readOnly}
            min={0}
            step={1}
            data-testid={`number-input-${name}`}
            style={{
              flex: 1,
              // Without this the input's intrinsic width wins inside a narrow
              // container (e.g. an `object` field group) and clips the unit badge.
              minWidth: 0,
              padding: '6px 8px',
              border: '1px solid #d0d4d8',
              borderRadius: 4,
              fontSize: 14,
            }}
          />
          {unit && (
            <span
              data-testid={`number-unit-${name}`}
              style={{
                padding: '6px 8px',
                background: '#f0f2f4',
                borderRadius: 4,
                fontSize: 12,
                color: '#646d72',
                fontWeight: 500,
              }}
            >
              {unit}
            </span>
          )}
        </div>
      </FieldLabel>
    );
  };
}
